import type { ActiveRequestInit } from '../../../api/active/roleViewDispatch';
import type { ActiveTransport } from '../../../api/active/transport';
import type {
  AssignmentWrite,
  AuditEvent,
  ManagedRole,
  ManagementCatalog,
  ManagementScope,
  RoleAssignment,
  RoleDraft,
  RoleModulePolicy,
  ScopeType,
} from '../../../permission-integration/managementModel';
import type {
  AdministratedDepartment,
  AdministratedUser,
  RolePolicyWrite,
} from '../../../permission-integration/permissionAdminApi';
import { READ_ONLY_OBJECT_DIRECTORY } from '../state/businessFieldDirectory';

const STORAGE_KEY = 'cpq-local-database-demo:v1:permissions';
const DEMO_NOW = '2026-10-27T00:00:00.000Z';
const DEMO_USER_ID = 'offline-demo-user';
const DEMO_ROLE_ID = 'local-demo-role-sales';
const DEMO_DEPARTMENT_ID = 'offline-demo-department';

const PERMISSION_MODULE_BY_OWNER: Record<string, string> = {
  'quote-header': 'quote',
  'quote-line': 'quote',
  channel: 'quote',
  device: 'equipment',
  package: 'package',
  plan: 'plan',
  customer: 'customer',
  inventory: 'inventory',
};

type LocalUser = AdministratedUser & {
  authorizationSource?: 'legacy' | 'assignments';
  assignmentCount?: number;
  readOnlyReason?: string | null;
};

type PublishedRole = {
  publishedVersion: string;
  name: string;
  active: boolean;
  publishedAt: string;
  modulePolicies: RoleModulePolicy[];
};

type RawAssignmentAudit = {
  actorUserId: string;
  occurredAt: string;
  action: string;
  resultCode: string;
  before: unknown;
  after: unknown;
};

type LocalState = {
  users: LocalUser[];
  departments: AdministratedDepartment[];
  roles: ManagedRole[];
  drafts: Record<string, RoleDraft>;
  previews: Record<string, { draftRowVersion: string; previewDigest: string }>;
  versions: Record<string, PublishedRole[]>;
  assignments: RoleAssignment[];
  roleAudits: Record<string, AuditEvent[]>;
  assignmentAudits: Record<string, RawAssignmentAudit[]>;
  sessionRevocations: number;
};

const jsonClone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

function scope(
  resourceType: string,
  scopeType: ScopeType = 'all',
): ManagementScope {
  return { resourceType, scopeType };
}

function fieldsForModule(moduleKey: string) {
  return READ_ONLY_OBJECT_DIRECTORY.flatMap((group) => {
    if (PERMISSION_MODULE_BY_OWNER[group.owner] !== moduleKey) return [];
    return group.fields
      .filter((field) => field.available)
      .map((field) => ({
        fieldKey: field.source,
        label: field.label,
        active: true,
        supportedModes: ['view', 'edit', 'export'] as Array<
          'view' | 'edit' | 'export'
        >,
      }));
  });
}

function demoPolicies(): RoleModulePolicy[] {
  const operations: Record<string, string[]> = {
    quote: ['quote.read', 'quote.edit', 'quote.share', 'quote.export'],
    equipment: ['equipment.read', 'equipment.edit'],
    package: ['package.read', 'package.edit'],
    plan: ['plan.read', 'plan.edit'],
    customer: ['customer.read', 'customer.edit'],
    inventory: ['inventory.read', 'inventory.adjust'],
    catalog: ['catalog.read', 'catalog.edit'],
    settings: ['settings.read', 'settings.edit'],
    identity: ['identity.user.read', 'identity.role.read'],
  };
  return Object.entries(operations).map(([moduleKey, operationCodes]) => ({
    moduleKey,
    registryVersion: `local-demo-${moduleKey}-v1`,
    operationCodes,
    operationDependencies: [],
    operationScopes: operationCodes.map((operationCode) => ({
      operationCode,
      ...scope(
        moduleKey === 'customer'
          ? 'customer'
          : moduleKey === 'catalog'
            ? 'catalog'
            : moduleKey,
      ),
    })),
    dataScopes: [
      scope(
        moduleKey === 'customer'
          ? 'customer'
          : moduleKey === 'catalog'
            ? 'catalog'
            : moduleKey,
      ),
    ],
    abac: {},
    fields: fieldsForModule(moduleKey).map((field) => ({
      fieldKey: field.fieldKey,
      view: true,
      edit: true,
      export: true,
    })),
    memberDenials: [],
    channelPolicies: [],
  }));
}

function initialState(): LocalState {
  const policies = demoPolicies();
  const role: ManagedRole = {
    id: DEMO_ROLE_ID,
    key: 'local_demo_sales',
    name: '本地演示销售岗位',
    systemRoleType: null,
    active: true,
    permissionCodes: policies.flatMap((item) => item.operationCodes),
    permissionDenials: [],
    stateActionDenials: [],
    dataScopes: policies.flatMap((item) => item.dataScopes),
    fieldPolicies: policies.flatMap((policy) =>
      policy.fields.map((field) => ({
        ...field,
        moduleKey: policy.moduleKey,
      })),
    ),
    stateActions: [],
    permissionVersion: '1',
    rowVersion: '1',
    authorizationSource: 'model3',
    protected: false,
    readOnlyReason: null,
    businessPolicyEditable: true,
    businessRoleAssignable: true,
  };
  const user: LocalUser = {
    userId: DEMO_USER_ID,
    wecomUserId: null,
    companyId: 'offline-demo-company',
    displayName: '本地演示用户',
    wecomDepartments: [
      { id: DEMO_DEPARTMENT_ID, path: '本地演示组织 / 报价组' },
    ],
    status: 'active',
    roleIds: [DEMO_ROLE_ID],
    departmentIds: [DEMO_DEPARTMENT_ID],
    primaryDepartmentId: DEMO_DEPARTMENT_ID,
    permissionVersion: '1',
    rowVersion: '1',
    createdAt: DEMO_NOW,
    updatedAt: DEMO_NOW,
    authorizationSource: 'assignments',
    assignmentCount: 1,
  };
  const department: AdministratedDepartment = {
    id: DEMO_DEPARTMENT_ID,
    code: 'LOCAL-DEMO',
    name: '本地演示报价组',
    active: true,
    assignmentRoleId: DEMO_ROLE_ID,
    assignmentRoleActive: true,
  };
  const assignment: RoleAssignment = {
    assignmentId: 'local-demo-assignment-1',
    userId: DEMO_USER_ID,
    roleId: DEMO_ROLE_ID,
    assignmentDepartmentId: DEMO_DEPARTMENT_ID,
    managedDepartmentIds: null,
    scopes: [
      scope('quote'),
      scope('equipment'),
      scope('package'),
      scope('plan'),
      scope('customer'),
      scope('inventory'),
      scope('catalog'),
      scope('settings'),
    ],
    validFrom: DEMO_NOW,
    validUntil: null,
    status: 'enabled',
    effectiveStatus: 'effective',
    basis: '本地演示初始授权，可在权限页面调整',
    grantorUserId: DEMO_USER_ID,
    rowVersion: '1',
    createdAt: DEMO_NOW,
    updatedAt: DEMO_NOW,
  };
  const draft: RoleDraft = {
    roleId: role.id,
    key: role.key,
    name: role.name,
    active: role.active,
    systemRoleType: null,
    status: 'draft',
    draftRowVersion: '1',
    currentPublishedVersion: '1',
    companyPermissionVersion: '1',
    authorizationModelVersion: '3',
    modulePolicies: policies,
  };
  return {
    users: [user],
    departments: [department],
    roles: [role],
    drafts: { [role.id]: draft },
    previews: {},
    versions: {
      [role.id]: [
        {
          publishedVersion: '1',
          name: role.name,
          active: role.active,
          publishedAt: DEMO_NOW,
          modulePolicies: policies,
        },
      ],
    },
    assignments: [assignment],
    roleAudits: { [role.id]: [] },
    assignmentAudits: { [assignment.assignmentId]: [] },
    sessionRevocations: 0,
  };
}

function readState(): LocalState {
  if (typeof window === 'undefined') return initialState();
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) return initialState();
    const value = JSON.parse(saved) as Partial<LocalState>;
    const seed = initialState();
    return {
      ...seed,
      ...value,
      users: Array.isArray(value.users) ? value.users : seed.users,
      departments: Array.isArray(value.departments)
        ? value.departments
        : seed.departments,
      roles: Array.isArray(value.roles) ? value.roles : seed.roles,
      drafts: value.drafts ?? seed.drafts,
      previews: value.previews ?? seed.previews,
      versions: value.versions ?? seed.versions,
      assignments: Array.isArray(value.assignments)
        ? value.assignments
        : seed.assignments,
      roleAudits: value.roleAudits ?? seed.roleAudits,
      assignmentAudits: value.assignmentAudits ?? seed.assignmentAudits,
      sessionRevocations: value.sessionRevocations ?? 0,
    };
  } catch {
    return initialState();
  }
}

function writeState(state: LocalState) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent('cpq-local-demo-modules-changed'));
  }
}

function response(value: unknown, status = 200, etag?: string) {
  return new Response(JSON.stringify(value), {
    status,
    headers: {
      'Content-Type': 'application/json',
      ...(etag ? { ETag: `"${etag}"` } : {}),
    },
  });
}

function page<T>(items: T[], url: URL) {
  const currentPage = Math.max(1, Number(url.searchParams.get('page')) || 1);
  const pageSize = Math.max(1, Number(url.searchParams.get('pageSize')) || 20);
  const start = (currentPage - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    meta: {
      page: currentPage,
      pageSize,
      total: items.length,
      totalPages: Math.ceil(items.length / pageSize),
    },
  };
}

function bodyOf(init: ActiveRequestInit): Record<string, any> {
  if (typeof init.body !== 'string') return {};
  try {
    const parsed = JSON.parse(init.body) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, any>)
      : {};
  } catch {
    return {};
  }
}

function nextVersion(value: string | undefined) {
  const parsed = Number(value);
  return String(Number.isFinite(parsed) ? parsed + 1 : 2);
}

function ifMatchMatches(init: ActiveRequestInit, version: string) {
  const supplied = new Headers(init.headers).get('if-match');
  return !supplied || supplied.replaceAll('"', '') === version;
}

function notFound(message: string) {
  return response({ code: 'NOT_FOUND', detail: message }, 404);
}

function conflict(message: string) {
  return response({ code: 'VERSION_CONFLICT', detail: message }, 409);
}

function managementCatalog(): ManagementCatalog {
  const scopeTypes: ScopeType[] = [
    'all',
    'self',
    'self_assigned',
    'department',
    'department_and_descendants',
    'specified_departments',
  ];
  const modules: ManagementCatalog['modules'] = demoPolicies().map(
    (policy) => ({
      moduleKey: policy.moduleKey,
      active: true,
      registryVersion: policy.registryVersion,
      operations: policy.operationCodes,
      resourceType: policy.dataScopes[0]?.resourceType,
      supportedScopeTypes: scopeTypes,
      operationScopeTypes: Object.fromEntries(
        policy.operationCodes.map((operationCode) => [
          operationCode,
          ['all', 'self', 'department', 'specified_departments'],
        ]),
      ) as Record<string, ScopeType[]>,
      channelPolicies: [],
      operationDependencies: [],
      fieldDictionary: fieldsForModule(policy.moduleKey),
      exportOperationCode: null,
      abac: {},
    }),
  );
  const codes = modules.flatMap((item) => item.operations);
  return {
    items: codes.map((code) => ({
      code,
      description: `本地演示权限：${code}`,
    })),
    modules,
    dataScopeTypes: ['none', ...scopeTypes],
    managementCapabilities: {
      readAssignments: true,
      manageUsers: true,
      manageStatus: true,
      manageOrganization: true,
      revokeSessions: true,
      manageAssignments: true,
      editRolePolicies: true,
      publishRolePolicies: true,
    },
  };
}

function assignmentEffectiveStatus(
  assignment: RoleAssignment,
): RoleAssignment['effectiveStatus'] {
  if (assignment.status === 'revoked') return 'revoked';
  if (assignment.status === 'suspended') return 'suspended';
  const now = Date.now();
  if (Date.parse(assignment.validFrom) > now) return 'future';
  if (assignment.validUntil && Date.parse(assignment.validUntil) <= now)
    return 'expired';
  return 'effective';
}

function mergeRoleWrite(role: ManagedRole, body: RolePolicyWrite): ManagedRole {
  return {
    ...role,
    key: body.key,
    name: body.name,
    active: body.active,
    permissionCodes: body.permissionCodes,
    permissionDenials: body.permissionDenials ?? [],
    stateActionDenials: body.stateActionDenials ?? [],
    dataScopes: body.dataScopes,
    fieldPolicies: body.fieldPolicies,
    stateActions: body.stateActions,
    rowVersion: nextVersion(role.rowVersion),
    permissionVersion: nextVersion(role.permissionVersion),
  };
}

function rawAuditEvent(
  action: string,
  resultCode = 'ASSIGNMENT_UPDATED',
): RawAssignmentAudit {
  return {
    actorUserId: DEMO_USER_ID,
    occurredAt: new Date().toISOString(),
    action,
    resultCode,
    before: null,
    after: null,
  };
}

function roleAuditEvent(action: string): AuditEvent {
  const labels: Record<string, string> = {
    'identity.update-role-policy': '更新演示角色权限',
    'identity.save-role-draft': '保存演示角色草稿',
    'identity.publish-role-policy': '发布演示角色策略',
  };
  return {
    actorUserId: DEMO_USER_ID,
    occurredAt: new Date().toISOString(),
    objectType: labels[action] ?? '角色权限变更',
    result: '已记录',
  };
}

function localResponse(state: LocalState, url: URL, init: ActiveRequestInit) {
  const path = url.pathname;
  const method = (init.method ?? 'GET').toUpperCase();
  const payload = bodyOf(init);

  if (path === '/permissions' && method === 'GET')
    return response(managementCatalog());

  if (path === '/departments' && method === 'GET')
    return response({ items: state.departments });

  if (path === '/users:sync-wecom' && method === 'POST') {
    const result = {
      created: 0,
      updated: 0,
      unchanged: state.users.length,
      total: state.users.length,
      localDemo: true,
    };
    return response(result);
  }

  if (path === '/users' && method === 'GET') {
    const search = (url.searchParams.get('search') ?? '')
      .trim()
      .toLocaleLowerCase();
    const departmentId = url.searchParams.get('wecomDepartmentId');
    const users = state.users.filter(
      (user) =>
        (!search || user.displayName.toLocaleLowerCase().includes(search)) &&
        (!departmentId ||
          user.wecomDepartments.some(
            (department) => department.id === departmentId,
          )),
    );
    return response({
      ...page(users, url),
      wecomDepartmentOptions: state.departments.map((department) => ({
        id: department.id,
        path: `本地演示组织 / ${department.name}`,
      })),
    });
  }

  if (path === '/users' && method === 'POST') {
    const id = `local-demo-user-${crypto.randomUUID()}`;
    const user: LocalUser = {
      userId: id,
      wecomUserId: String(payload.wecomUserId ?? ''),
      companyId: 'offline-demo-company',
      displayName: String(payload.displayName ?? '本地演示成员'),
      wecomDepartments: (
        (payload.departmentIds as string[] | undefined) ?? []
      ).map((departmentId) => ({
        id: departmentId,
        path: `本地演示组织 / ${state.departments.find((item) => item.id === departmentId)?.name ?? departmentId}`,
      })),
      status: 'pending_authorization',
      roleIds: [],
      departmentIds: payload.departmentIds ?? [],
      primaryDepartmentId: payload.primaryDepartmentId ?? null,
      permissionVersion: '1',
      rowVersion: '1',
      createdAt: DEMO_NOW,
      updatedAt: DEMO_NOW,
      authorizationSource: 'assignments',
      assignmentCount: 0,
    };
    state.users.push(user);
    writeState(state);
    return response(user, 201, user.rowVersion);
  }

  const effectiveMatch = /^\/users\/([^/]+)\/effective-authorization$/.exec(
    path,
  );
  if (effectiveMatch && method === 'GET') {
    const id = decodeURIComponent(effectiveMatch[1]);
    if (!state.users.some((user) => user.userId === id))
      return notFound('没有这个本地演示成员');
    const assignments = state.assignments
      .filter(
        (item) =>
          item.userId === id && assignmentEffectiveStatus(item) === 'effective',
      )
      .map((item) => ({
        ...item,
        effectiveStatus: assignmentEffectiveStatus(item),
      }));
    return response({
      userId: id,
      authorizationSource: 'assignments',
      assignments,
      warnings: ['这是浏览器内演示授权，不会改变登录会话或后端权限。'],
    });
  }

  const userMatch = /^\/users\/([^/]+)$/.exec(path);
  if (userMatch) {
    const id = decodeURIComponent(userMatch[1]);
    const index = state.users.findIndex((item) => item.userId === id);
    const current = state.users[index];
    if (!current) return notFound('没有这个本地演示成员');
    if (method === 'GET') return response(current, 200, current.rowVersion);
    if (method === 'PATCH') {
      if (!ifMatchMatches(init, current.rowVersion))
        return conflict('成员已在本地演练中更新，请重新读取后再保存');
      const next: LocalUser = {
        ...current,
        ...(typeof payload.displayName === 'string'
          ? { displayName: payload.displayName }
          : {}),
        ...(payload.status ? { status: payload.status } : {}),
        ...(Array.isArray(payload.departmentIds)
          ? { departmentIds: payload.departmentIds }
          : {}),
        ...(typeof payload.primaryDepartmentId === 'string' ||
        payload.primaryDepartmentId === null
          ? { primaryDepartmentId: payload.primaryDepartmentId }
          : {}),
        ...(Array.isArray(payload.roleIds) ? { roleIds: payload.roleIds } : {}),
        rowVersion: nextVersion(current.rowVersion),
        permissionVersion: nextVersion(current.permissionVersion),
        updatedAt: new Date().toISOString(),
      };
      if (payload.revokeAllSessions === true) state.sessionRevocations += 1;
      state.users[index] = next;
      writeState(state);
      return response(next, 200, next.rowVersion);
    }
  }

  if (path === '/roles' && method === 'GET')
    return response({ items: state.roles });

  if (path === '/roles/drafts' && method === 'POST') {
    if (state.roles.some((role) => role.key === payload.key))
      return conflict('本地演练中已有同名角色标识');
    const id = `local-demo-role-${crypto.randomUUID()}`;
    const policies = demoPolicies();
    const draft: RoleDraft = {
      roleId: id,
      key: String(payload.key ?? id),
      name: String(payload.name ?? '本地演示角色'),
      active: payload.active !== false,
      systemRoleType: null,
      status: 'draft',
      draftRowVersion: '1',
      currentPublishedVersion: null,
      companyPermissionVersion: '1',
      authorizationModelVersion: '3',
      modulePolicies: policies,
    };
    const role: ManagedRole = {
      id,
      key: draft.key,
      name: draft.name,
      systemRoleType: null,
      active: draft.active,
      permissionCodes: [],
      permissionDenials: [],
      stateActionDenials: [],
      dataScopes: [],
      fieldPolicies: [],
      stateActions: [],
      permissionVersion: '1',
      rowVersion: '1',
      authorizationSource: 'model3',
      protected: false,
      readOnlyReason: null,
      businessPolicyEditable: true,
      businessRoleAssignable: false,
    };
    state.roles.push(role);
    state.drafts[id] = draft;
    state.versions[id] = [];
    state.roleAudits[id] = [];
    writeState(state);
    return response(draft, 201, draft.draftRowVersion);
  }

  const legacyRoleMatch = /^\/roles\/([^/]+)$/.exec(path);
  if (legacyRoleMatch) {
    const id = decodeURIComponent(legacyRoleMatch[1]);
    const index = state.roles.findIndex((item) => item.id === id);
    const current = state.roles[index];
    if (!current) return notFound('没有这个本地演示角色');
    if (method === 'PATCH') {
      if (!ifMatchMatches(init, current.rowVersion))
        return conflict('角色已在本地演练中更新，请重新读取后再保存');
      const updated = mergeRoleWrite(
        current,
        payload as unknown as RolePolicyWrite,
      );
      state.roles[index] = updated;
      const draft = state.drafts[id];
      if (draft) {
        draft.key = updated.key;
        draft.name = updated.name;
        draft.active = updated.active;
        draft.draftRowVersion = nextVersion(draft.draftRowVersion);
        draft.modulePolicies = draft.modulePolicies.map((policy) => ({
          ...policy,
          operationCodes: policy.operationCodes.filter((code) =>
            updated.permissionCodes.includes(code),
          ),
        }));
      }
      state.roleAudits[id] = [
        roleAuditEvent('identity.update-role-policy'),
        ...(state.roleAudits[id] ?? []),
      ];
      writeState(state);
      return response(updated, 200, updated.rowVersion);
    }
  }

  const rolePathMatch =
    /^\/roles\/([^/]+)\/(draft|draft:preview|draft:publish|published-versions|audit-events)$/.exec(
      path,
    );
  if (rolePathMatch) {
    const id = decodeURIComponent(rolePathMatch[1]);
    const action = rolePathMatch[2];
    const draft = state.drafts[id];
    const role = state.roles.find((item) => item.id === id);
    if (!role) return notFound('没有这个本地演示角色');
    if (action === 'published-versions' && method === 'GET')
      return response({ items: state.versions[id] ?? [] });
    if (action === 'audit-events' && method === 'GET')
      return response(page(state.roleAudits[id] ?? [], url));
    if (!draft) return notFound('该角色尚无本地演示草稿');
    if (action === 'draft' && method === 'GET')
      return response(draft, 200, draft.draftRowVersion);
    if (action === 'draft' && method === 'PUT') {
      if (!ifMatchMatches(init, draft.draftRowVersion))
        return conflict('角色草稿已在本地演练中更新，请重新读取后再保存');
      const updated: RoleDraft = {
        ...draft,
        name: String(payload.name ?? draft.name),
        active:
          typeof payload.active === 'boolean' ? payload.active : draft.active,
        modulePolicies: Array.isArray(payload.modulePolicies)
          ? payload.modulePolicies
          : draft.modulePolicies,
        draftRowVersion: nextVersion(draft.draftRowVersion),
      };
      state.drafts[id] = updated;
      delete state.previews[id];
      state.roleAudits[id] = [
        roleAuditEvent('identity.save-role-draft'),
        ...(state.roleAudits[id] ?? []),
      ];
      writeState(state);
      return response(updated, 200, updated.draftRowVersion);
    }
    if (action === 'draft:preview' && method === 'POST') {
      if (!ifMatchMatches(init, draft.draftRowVersion))
        return conflict('角色草稿已更新，请重新加载后再预览');
      const previewDigest = `local-demo-${draft.roleId}-${draft.draftRowVersion}`;
      state.previews[id] = {
        draftRowVersion: draft.draftRowVersion,
        previewDigest,
      };
      const activeCodes = new Set(role.permissionCodes);
      const proposedCodes = new Set(
        draft.modulePolicies.flatMap((item) => item.operationCodes),
      );
      const changes = [...new Set([...activeCodes, ...proposedCodes])]
        .filter((code) => activeCodes.has(code) !== proposedCodes.has(code))
        .map((operationCode) => ({
          operationCode,
          before: JSON.stringify({ enabled: activeCodes.has(operationCode) }),
          after: JSON.stringify({ enabled: proposedCodes.has(operationCode) }),
        }));
      writeState(state);
      return response({
        draftRowVersion: draft.draftRowVersion,
        previewDigest,
        changes,
        affectedUserIds: state.users
          .filter((user) => user.roleIds.includes(id))
          .map((user) => user.userId),
        blockers: [],
      });
    }
    if (action === 'draft:publish' && method === 'POST') {
      const preview = state.previews[id];
      if (
        !preview ||
        payload.draftRowVersion !== draft.draftRowVersion ||
        payload.previewDigest !== preview.previewDigest
      )
        return conflict('请先预览当前草稿，再发布演示策略');
      const currentVersion = state.versions[id]?.length ?? 0;
      const publishedVersion = String(currentVersion + 1);
      const updated: ManagedRole = {
        ...role,
        key: draft.key,
        name: draft.name,
        active: draft.active,
        permissionCodes: draft.modulePolicies.flatMap(
          (item) => item.operationCodes,
        ),
        dataScopes: draft.modulePolicies.flatMap((item) => item.dataScopes),
        fieldPolicies: draft.modulePolicies.flatMap((item) =>
          item.fields.map((field) => ({ ...field, moduleKey: item.moduleKey })),
        ),
        rowVersion: nextVersion(role.rowVersion),
        permissionVersion: publishedVersion,
        businessRoleAssignable: draft.active,
      };
      state.roles = state.roles.map((item) =>
        item.id === id ? updated : item,
      );
      state.versions[id] = [
        ...(state.versions[id] ?? []),
        {
          publishedVersion,
          name: draft.name,
          active: draft.active,
          publishedAt: new Date().toISOString(),
          modulePolicies: jsonClone(draft.modulePolicies),
        },
      ];
      state.drafts[id] = {
        ...draft,
        currentPublishedVersion: publishedVersion,
      };
      delete state.previews[id];
      state.roleAudits[id] = [
        roleAuditEvent('identity.publish-role-policy'),
        ...(state.roleAudits[id] ?? []),
      ];
      writeState(state);
      return response({ publishedVersion });
    }
  }

  if (path === '/role-assignments' && method === 'GET') {
    const userId = url.searchParams.get('userId');
    const roleId = url.searchParams.get('roleId');
    const status = url.searchParams.get('status');
    const items = state.assignments.filter(
      (item) =>
        (!userId || item.userId === userId) &&
        (!roleId || item.roleId === roleId) &&
        (!status || item.status === status),
    );
    return response(
      page(
        items.map((item) => ({
          ...item,
          effectiveStatus: assignmentEffectiveStatus(item),
        })),
        url,
      ),
    );
  }

  if (path === '/role-assignments' && method === 'POST') {
    const write = payload as unknown as AssignmentWrite;
    const now = new Date().toISOString();
    const assignment: RoleAssignment = {
      ...write,
      assignmentId: `local-demo-assignment-${crypto.randomUUID()}`,
      managedDepartmentIds: write.managedDepartmentIds ?? null,
      validUntil: write.validUntil ?? null,
      status: 'enabled',
      effectiveStatus: 'future',
      grantorUserId: DEMO_USER_ID,
      rowVersion: '1',
      createdAt: now,
      updatedAt: now,
    };
    assignment.effectiveStatus = assignmentEffectiveStatus(assignment);
    state.assignments.push(assignment);
    state.assignmentAudits[assignment.assignmentId] = [
      rawAuditEvent('identity.create-role-assignment'),
    ];
    const targetUser = state.users.find(
      (user) => user.userId === assignment.userId,
    );
    if (targetUser) {
      targetUser.assignmentCount = (targetUser.assignmentCount ?? 0) + 1;
      if (!targetUser.roleIds.includes(assignment.roleId))
        targetUser.roleIds.push(assignment.roleId);
      targetUser.rowVersion = nextVersion(targetUser.rowVersion);
      targetUser.permissionVersion = nextVersion(targetUser.permissionVersion);
    }
    writeState(state);
    return response(assignment, 201, assignment.rowVersion);
  }

  const assignmentAuditMatch =
    /^\/role-assignments\/([^/]+)\/audit-events$/.exec(path);
  if (assignmentAuditMatch && method === 'GET') {
    const id = decodeURIComponent(assignmentAuditMatch[1]);
    if (!state.assignments.some((item) => item.assignmentId === id))
      return notFound('没有这条本地演示授权');
    return response(page(state.assignmentAudits[id] ?? [], url));
  }

  const revokeMatch = /^\/role-assignments\/([^/]+):revoke$/.exec(path);
  if (revokeMatch && method === 'POST') {
    const id = decodeURIComponent(revokeMatch[1]);
    const index = state.assignments.findIndex(
      (item) => item.assignmentId === id,
    );
    if (index < 0) return notFound('没有这条本地演示授权');
    const current = state.assignments[index];
    if (!ifMatchMatches(init, current.rowVersion))
      return conflict('授权已更新，请重新读取后再撤销');
    const updated: RoleAssignment = {
      ...current,
      status: 'revoked',
      effectiveStatus: 'revoked',
      rowVersion: nextVersion(current.rowVersion),
      updatedAt: new Date().toISOString(),
    };
    state.assignments[index] = updated;
    const user = state.users.find((item) => item.userId === updated.userId);
    if (user) {
      const stillAssigned = state.assignments.some(
        (item) =>
          item.userId === updated.userId &&
          item.roleId === updated.roleId &&
          assignmentEffectiveStatus(item) === 'effective',
      );
      if (!stillAssigned)
        user.roleIds = user.roleIds.filter(
          (roleId) => roleId !== updated.roleId,
        );
      user.assignmentCount = state.assignments.filter(
        (item) =>
          item.userId === updated.userId &&
          assignmentEffectiveStatus(item) === 'effective',
      ).length;
      user.rowVersion = nextVersion(user.rowVersion);
      user.permissionVersion = nextVersion(user.permissionVersion);
    }
    state.assignmentAudits[id] = [
      rawAuditEvent('identity.revoke-role-assignment'),
      ...(state.assignmentAudits[id] ?? []),
    ];
    writeState(state);
    return response(updated, 200, updated.rowVersion);
  }

  const assignmentMatch = /^\/role-assignments\/([^/]+)$/.exec(path);
  if (assignmentMatch) {
    const id = decodeURIComponent(assignmentMatch[1]);
    const index = state.assignments.findIndex(
      (item) => item.assignmentId === id,
    );
    const current = state.assignments[index];
    if (!current) return notFound('没有这条本地演示授权');
    if (method === 'GET')
      return response(
        { ...current, effectiveStatus: assignmentEffectiveStatus(current) },
        200,
        current.rowVersion,
      );
    if (method === 'PATCH') {
      if (!ifMatchMatches(init, current.rowVersion))
        return conflict('授权已更新，请重新读取后再保存');
      const updated: RoleAssignment = {
        ...current,
        ...payload,
        rowVersion: nextVersion(current.rowVersion),
        updatedAt: new Date().toISOString(),
      };
      updated.effectiveStatus = assignmentEffectiveStatus(updated);
      state.assignments[index] = updated;
      state.assignmentAudits[id] = [
        rawAuditEvent('identity.update-role-assignment'),
        ...(state.assignmentAudits[id] ?? []),
      ];
      writeState(state);
      return response(updated, 200, updated.rowVersion);
    }
  }

  return undefined;
}

/** Local-only simulation for currently implemented permission-management APIs. */
export function createLocalPermissionTransport(
  base: ActiveTransport,
): ActiveTransport {
  const requestResponse = async (
    path: string,
    init: ActiveRequestInit = {},
  ) => {
    const url = new URL(
      path,
      typeof window === 'undefined'
        ? 'http://localhost'
        : window.location.origin,
    );
    if (init.signal?.aborted) throw init.signal.reason;
    const state = readState();
    const result = localResponse(state, url, init);
    if (result) return result;
    return base.requestResponse(path, init);
  };
  const request = async <T>(path: string, init: ActiveRequestInit = {}) => {
    const result = await requestResponse(path, init);
    if (!result.ok) {
      const problem = (await result.json().catch(() => ({}))) as {
        detail?: string;
        title?: string;
      };
      throw new Error(
        problem.detail ??
          problem.title ??
          `本地权限演练请求失败（HTTP ${result.status}）`,
      );
    }
    if (result.status === 204) return undefined as T;
    return (await result.json()) as T;
  };
  return { ...base, requestResponse, request };
}
